"use client";

import React, { useRef, useState, useEffect, useMemo, Fragment } from "react";
import { useNetworkBadge } from "@/app/lib/useNetworkBadge";
import dynamic from "next/dynamic";
import { Popover, Transition } from "@headlessui/react";
import Avatar from "@/shared/Avatar";
import SwitchDarkMode2 from "@/shared/SwitchDarkMode2";
import Link from "next/link";
import { useAppSelector } from '@/app/lib/hooks';
import api from "@/app/lib/reducers/api";
import useClickOutside from '@/hooks/useClickOutside';
import { Route } from "next";
import { useParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import GuydeezCoins from "./GuydeezCoins";
import { clearActingBackups } from "@/app/lib/clearActingBackups";

// Lazy-loaded — the admin search modal is only ever opened by role-1
// users and we don't want its bundle (Headless UI Dialog + the user
// search axios call) sitting in the initial AvatarDropdown chunk for
// everyone else.
const ImpersonationModal = dynamic(
  () => import("@/components/admin/ImpersonationModal"),
  { ssr: false, loading: () => null }
);

interface Props {
  className?: string;
}

export default function AvatarDropdown({ className = "" }: Props) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const user = useAppSelector((state) => state.auth.user);
  const netBadge = useNetworkBadge(user);
  const { t } = useTranslation("guydeez");
  const params = useParams<{ currency?: string }>();
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  // Role 1 = super admin. The full check covers both shapes the
  // backend emits the role under (top-level number + nested .id).
  const isSuperAdmin =
    user?.role === 1 || user?.roleDetails?.id === 1;

  // Who may take over an account: role 1 always; role 2/4 ONLY with an active
  // grant in the impersonationRights table (the backend owns that decision and
  // exposes it here). Any other role never can. Drives the menu item below.
  const roleId = (user?.role ?? user?.roleDetails?.id) as number | undefined;
  const [canImpersonate, setCanImpersonate] = useState(false);
  useEffect(() => {
    if (isSuperAdmin) {
      setCanImpersonate(true);
      return;
    }
    if (roleId !== 2 && roleId !== 4) {
      setCanImpersonate(false);
      return;
    }
    let cancelled = false;
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("access_token")
        : null;
    if (!token) {
      setCanImpersonate(false);
      return;
    }
    fetch(
      `${process.env.NEXT_PUBLIC_SOCKET}/api/admin/impersonate/eligibility`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => (r.ok ? r.json() : { canImpersonate: false }))
      .then((d) => {
        if (!cancelled) setCanImpersonate(Boolean(d?.canImpersonate));
      })
      .catch(() => {
        if (!cancelled) setCanImpersonate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, roleId]);

  async function logout(){
    // Dans l'app mobile, delier l'appareil AVANT d'effacer le jeton d'acces —
    // l'appel a besoin d'etre authentifie. Sans ca, le compte suivant sur ce
    // telephone continue de recevoir les notifications push de celui-ci
    // jusqu'a ce qu'il se connecte lui-meme.
    //
    // Borne a 1,5 s et jamais bloquant : une deconnexion est locale, elle ne
    // doit pas dependre du reseau. En navigateur web classique, le pont est
    // absent et il ne se passe rien.
    try {
      const bridge = (window as any).__GUYDEEZ_APP__;
      const installationId = bridge?.installationId;
      const pushToken = bridge?.fcmToken;
      if (installationId || pushToken) {
        await api.delete('/api/me/devices', {
          data: { installationId, token: pushToken },
          timeout: 1500,
        });
      }
    } catch {
      // Reseau coupe, appel trop lent, session deja expiree : on se deconnecte
      // quand meme. La ligne restera active en base jusqu'a la prochaine
      // connexion sur cet appareil, qui la reattribuera.
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    clearActingBackups();
    window.location.href = "/login";
  };

  const handleClose = () => {
    const button = dropdownRef.current?.querySelector('button[data-headlessui-state="open"]') as HTMLElement;
    button?.click();
  };

  // Hook to close the dropdown when clicking outside of it
  useClickOutside(dropdownRef, handleClose);

  return (
    <div ref={dropdownRef} className={`relative flex ${className}`}>
      <Popover style={{ display: 'contents' }}>
        {({ open, close }) => (
          <Fragment>
            <Popover.Button
              aria-label={t('user_menu', { defaultValue: 'User menu' }) as string}
              className={`self-center w-10 h-10 sm:w-12 sm:h-12 rounded-full text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none flex items-center justify-center`}
            >
              <Avatar
                sizeClass="w-8 h-8 sm:w-9 sm:h-9"
                radius="rounded-full"
                userName={user.firstName}
                imgUrl={user.picture || undefined}
              />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-[260px] px-4 top-full -right-10 sm:right-0 sm:px-0">
                <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid grid-cols-1 gap-6 bg-white dark:bg-neutral-800 py-7 px-6 text-neutral-800 dark:text-neutral-200">
                    <div className="flex items-center space-x-3">
                      <Avatar
                        sizeClass="w-12 h-12"
                        radius="rounded-full"
                        userName={user.firstName}
                        imgUrl={user.picture || undefined}
                      />
                      <div className="flex-grow">
                        <h4 className="font-semibold">{user ? `${user.firstName} ${user.lastName}` : 'Guest'}</h4>
                        <p className="text-xs mt-0.5">{user ? `${user.roleDetails?.name}` : ''}</p>
                      </div>
                    </div>

                    <div className="w-full border-b border-neutral-200 dark:border-neutral-700" />

                    {user && (
                      <>
                        <GuydeezCoins onClose={() => close()} />
                        <div className="w-full border-b border-neutral-200 dark:border-neutral-700" />
                      </>
                    )}

                    {/* ------------------ 1 --------------------- */}
                    <Link
                      href={"/account" as Route} 
                      className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                      onClick={() => close()}
                    >
                      <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.1601 10.87C12.0601 10.86 11.9401 10.86 11.8301 10.87C9.45006 10.79 7.56006 8.84 7.56006 6.44C7.56006 3.99 9.54006 2 12.0001 2C14.4501 2 16.4401 3.99 16.4401 6.44C16.4301 8.84 14.5401 10.79 12.1601 10.87Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.15997 14.56C4.73997 16.18 4.73997 18.82 7.15997 20.43C9.90997 22.27 14.42 22.27 17.17 20.43C19.59 18.81 19.59 16.17 17.17 14.56C14.43 12.73 9.91997 12.73 7.15997 14.56Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium ">{t('my_account')}</p>
                      </div>
                    </Link>

                    {/* ------------------ Contact my manager --------------------- */}
                    {user.isPro === true &&
                      <Link
                        href={"/contact-pro" as Route}
                        className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                        onClick={() => close()}
                      >
                        <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.1601 10.87C12.0601 10.86 11.9401 10.86 11.8301 10.87C9.45006 10.79 7.56006 8.84 7.56006 6.44C7.56006 3.99 9.54006 2 12.0001 2C14.4501 2 16.4401 3.99 16.4401 6.44C16.4301 8.84 14.5401 10.79 12.1601 10.87Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M7.15997 14.56C4.73997 16.18 4.73997 18.82 7.15997 20.43C9.90997 22.27 14.42 22.27 17.17 20.43C19.59 18.81 19.59 16.17 17.17 14.56C14.43 12.73 9.91997 12.73 7.15997 14.56Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium ">{t('contact_my_manager')}</p>
                        </div>
                      </Link>
                    }

                    {/* ------------------ My Bookings --------------------- */}
                    <Link
                      href={"/bookings" as Route}
                      className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                      onClick={() => close()}
                    >
                      <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M8 12.2H15"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8 16.2H12.38"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 6H14C16 6 16 5 16 4C16 2 15 2 14 2H10C9 2 8 2 8 4C8 6 9 6 10 6Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 4.02002C19.33 4.20002 21 5.43002 21 10V16C21 20 20 22 15 22H9C4 22 3 20 3 16V10C3 5.44002 4.67 4.20002 8 4.02002"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium ">{t('my_bookings')}</p>
                      </div>
                    </Link>

                    {/* ------------------ Guide menu --------------------- */}
                    {(user.role === 1 || user.role === 2 || user.role === 3 || user.roleDetails?.id === 1 || user.roleDetails?.id === 2 || user.roleDetails?.id === 3) &&
                      <Link
                        href={"/guide-missions" as Route}
                        className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                        onClick={() => close()}
                      >
                        <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M8 12.2H15"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8 16.2H12.38"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10 6H14C16 6 16 5 16 4C16 2 15 2 14 2H10C9 2 8 2 8 4C8 6 9 6 10 6Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16 4.02002C19.33 4.20002 21 5.43002 21 10V16C21 20 20 22 15 22H9C4 22 3 20 3 16V10C3 5.44002 4.67 4.20002 8 4.02002"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium ">{t('guide_dashboard')}</p>
                        </div>
                      </Link>
                    }

                    {/* ------------------ My network --------------------- */}
                    {/* Shown to every pro account, not only head offices: this is where an
                        agency finds the invitation waiting for it, and accepting from its
                        own session IS the consent. */}
                    {netBadge.show &&
                      <Link
                        href={"/my-network" as Route}
                        className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                        onClick={() => close()}
                      >
                        <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 4.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M5.5 15.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4ZM18.5 15.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 8.5v3m0 0-5.2 4m5.2-4 5.2 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div className="ml-4 flex items-center gap-2">
                          <p className="text-sm font-medium ">{t('account_my_network')}</p>
                          {/* A pending request is the reason the entry showed up at all;
                              saying how many turns the menu into the notification. */}
                          {netBadge.pending > 0 && (
                            <span className="inline-flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 h-4 min-w-[16px] bg-primary-600 text-white">
                              {netBadge.pending}
                            </span>
                          )}
                        </div>
                      </Link>
                    }

                    {/* ------------------ Favorite tours --------------------- */}
                    <Link
                      href={"/favourite-tours" as Route}
                      className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                      onClick={() => close()}
                    >
                      <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12.62 20.81C12.28 20.93 11.72 20.93 11.38 20.81C8.48 19.82 2 15.69 2 8.68998C2 5.59998 4.49 3.09998 7.56 3.09998C9.38 3.09998 10.99 3.97998 12 5.33998C13.01 3.97998 14.63 3.09998 16.44 3.09998C19.51 3.09998 22 5.59998 22 8.68998C22 15.69 15.52 19.82 12.62 20.81Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium ">{t('my_favorites_tours')}</p>
                      </div>
                    </Link>

                    <div className="w-full border-b border-neutral-200 dark:border-neutral-700" />

                    {/* --------- Admin take-over (role 1, or role 2/4 with an
                        active impersonation grant) --------------------------- */}
                    {canImpersonate && (
                      <button
                        type="button"
                        onClick={() => {
                          close();
                          setImpersonationOpen(true);
                        }}
                        className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50 w-full text-left"
                      >
                        <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="3"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium">
                            {t("impersonation_menu_item", {
                              defaultValue: "Prise de contrôle",
                            })}
                          </p>
                        </div>
                      </button>
                    )}

                    {/* ------------------ 2 --------------------- */}
                    <div className="group flex items-center justify-between p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50">
                      <div className="flex items-center">
                        <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.0001 7.88989L10.9301 9.74989C10.6901 10.1599 10.8901 10.4999 11.3601 10.4999H12.6301C13.1101 10.4999 13.3001 10.8399 13.0601 11.2499L12.0001 13.1099"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8.30011 18.0399V16.8799C6.00011 15.4899 4.11011 12.7799 4.11011 9.89993C4.11011 4.94993 8.66011 1.06993 13.8001 2.18993C16.0601 2.68993 18.0401 4.18993 19.0701 6.25993C21.1601 10.4599 18.9601 14.9199 15.7301 16.8699V18.0299C15.7301 18.3199 15.8401 18.9899 14.7701 18.9899H9.26011C8.16011 18.9999 8.30011 18.5699 8.30011 18.0399Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8.5 22C10.79 21.35 13.21 21.35 15.5 22"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium ">{t('dark_theme')}</p>
                        </div>
                      </div>
                      <SwitchDarkMode2 />
                    </div>

                    {/* ------------------ 2 --------------------- */}
                    <Link
                      href={"/faq"}
                      className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                      onClick={() => close()}
                    >
                      <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.97 22C17.4928 22 21.97 17.5228 21.97 12C21.97 6.47715 17.4928 2 11.97 2C6.44715 2 1.97 6.47715 1.97 12C1.97 17.5228 6.44715 22 11.97 22Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 16.5C14.4853 16.5 16.5 14.4853 16.5 12C16.5 9.51472 14.4853 7.5 12 7.5C9.51472 7.5 7.5 9.51472 7.5 12C7.5 14.4853 9.51472 16.5 12 16.5Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4.89999 4.92993L8.43999 8.45993"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4.89999 19.07L8.43999 15.54"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M19.05 19.07L15.51 15.54"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M19.05 4.92993L15.51 8.45993"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium ">{t('faq')}</p>
                      </div>
                    </Link>

                    {/* ------------------ Delete my account ---------------
                        This dropdown IS the account menu on mobile — the
                        AccountShell sidebar is `hidden lg:flex`, so without an
                        entry here the deletion route would be desktop-only.
                        Sits last, under a divider, in coral: destructive, and
                        never adjacent to a routine navigation item. */}
                    <div className="w-full border-b border-neutral-200 dark:border-neutral-700" />

                    <Link
                      href={"/account/delete" as Route}
                      className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-red-600 dark:hover:bg-red-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-red-500 focus-visible:ring-opacity-50"
                      onClick={() => close()}
                    >
                      <div className="flex items-center justify-center flex-shrink-0 text-red-500 dark:text-red-400 group-hover:text-white">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 11v6M14 11v6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-red-600 dark:text-red-400 group-hover:text-white">
                          {t("account_nav_delete_my_account", {
                            defaultValue: "Supprimer mon compte",
                          })}
                        </p>
                      </div>
                    </Link>

                    {/* ------------------ 2 --------------------- */}
                    <Link
                      href={"/#"}
                      className="group flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-primary-600 dark:hover:bg-primary-700 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                      onClick={() => logout()}
                    >
                      <div className="flex items-center justify-center flex-shrink-0 text-neutral-500 dark:text-neutral-300 group-hover:text-white">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.90002 7.55999C9.21002 3.95999 11.06 2.48999 15.11 2.48999H15.24C19.71 2.48999 21.5 4.27999 21.5 8.74999V15.27C21.5 19.74 19.71 21.53 15.24 21.53H15.11C11.09 21.53 9.24002 20.08 8.91002 16.54"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M15 12H3.62"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M5.85 8.6499L2.5 11.9999L5.85 15.3499"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium">{t('logout')}</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </Fragment>
        )}
      </Popover>
      {/* Admin take-over modal — gated by `canImpersonate`, so its chunk only
          loads for users who may actually take over (role 1, or role 2/4 with
          an active grant). */}
      {canImpersonate && (
        <ImpersonationModal
          open={impersonationOpen}
          onClose={() => setImpersonationOpen(false)}
        />
      )}
    </div>
  );
}
